/**
 * Bảng màu trang Quản trị — dùng CHUNG bộ màu với app HP Media (bản thiết kế
 * 27/09/2026, HP-Media/src/app/themes/color.ts) để hai bên trông như một sản phẩm.
 *
 * Quy ước tương phản giống app: chữ/nút dùng `accent` (#C2337D, ≥4.5:1 trên nền
 * trắng); hồng thương hiệu `brand` (#EE93CA) chỉ để trang trí (viền, vòng, nền nhạt).
 */
export const colors = {
  // Nền & bề mặt
  bg: '#FAF7F8',
  surface: '#FFFFFF',
  surfaceMuted: '#F6F0F3',
  line: '#ECE4E9',
  divider: '#F3EDF1',

  // Chữ
  textPrimary: '#1D1D2E',
  textSecondary: '#6B6272',
  // Chỉ cho icon / chữ vô hiệu hoá: 3.1:1 trên nền trắng. Chữ phụ mang thông tin dùng textSecondary.
  textMuted: '#9A8F9E',

  // Nhấn
  brand: '#EE93CA',
  accent: '#C2337D',
  accentDeep: '#9A1F61',
  accentSoft: '#FCE7F1',
  accentLine: '#F3C9DE',

  // Dải màu Hoàng hôn (28/09/2026, cùng tên với app). Chỉ để trang trí (nền lem, quầng sáng);
  // nút vẫn là accent. Chữ trên lem nhạt: heading / inkSoft / accentDeep (textSecondary chỉ ~4:1,
  // accent ~3.9:1). Chữ trên nền Đêm (navy): trắng / onNight.
  sun: '#FFC078',
  peach: '#FEA88C',
  blush: '#F78FAE',
  orchid: '#EE86C8',
  lilac: '#B98AF0',
  heading: '#1B2150',
  navy: '#1B2150',
  navyInk: '#232C6E',
  inkSoft: '#3F4468',
  onNight: '#E6E8F7',
  sunSoft: '#FFF1DF',
  sunInk: '#9A4A0B',
  peachSoft: '#FFE9E2',
  peachInk: '#B3401F',
  tabActive: '#A3206C',

  // Trạng thái
  live: '#E11D48',
  successInk: '#0F7A46',
  successSoft: '#E6F8EE',
  warningInk: '#9A4A0B',
  warningSoft: '#FFF1E6',
  dangerInk: '#B42318',
  dangerSoft: '#FDE8E8',
  purple: '#8E97FD',
  // Tím nhạt (28/09, như app): nền #EFEBFF, chữ #5B3FD1 (5,9:1).
  purpleInk: '#5B3FD1',
  purpleSoft: '#EFEBFF',
  star: '#E8B04B',
  starInk: '#8A5A00',
  starSoft: '#FFF6E0',
  infoInk: '#1F5FA8',
  infoSoft: '#E6F0FB',

  // Thanh bên nền "Đêm" (28/09/2026, dải Hoàng hôn — trước đây tím than #221A2C): nền navy,
  // quầng cam/hồng/tím ở góc vẽ trên KHUNG thanh bên (lem.demSidebar); mục menu trong suốt,
  // mục đang chọn vẫn accent chữ trắng. Chữ: onNight / trắng (≥4.5:1 kể cả chỗ quầng sáng).
  sidebarBg: '#1B2150',
  // Nền nhóm con đang mở: lớp tối mờ để quầng sáng vẫn xuyên qua.
  sidebarBgDeep: 'rgba(10, 13, 40, 0.28)',
  sidebarLine: 'rgba(255, 255, 255, 0.08)',
  sidebarText: '#E6E8F7',
  sidebarTextMuted: 'rgba(230, 232, 247, 0.72)',
};

/**
 * Nền "lem" của dải Hoàng hôn — cùng thông số với LemFill của app (HP-Media/src/app/library/
 * components/ui/lem-fill.tsx). Bản SCSS: $lem-nhat / $lem-ruc / $lem-dem trong variables.scss.
 * - nhat: dải đầu thẻ, hero. Chữ: heading / inkSoft / accentDeep.
 * - ruc: khối nổi bật. Chữ chỉ dùng heading (chữ trắng ~2:1).
 * - dem: nền xanh đậm (cột thương hiệu trang đăng nhập). Chữ: trắng / onNight.
 */
export const lem = {
  nhat: 'radial-gradient(95% 130% at 0% 0%, #FFDDB5 0%, transparent 60%), radial-gradient(80% 130% at 38% 100%, #FFD0C4 0%, transparent 62%), radial-gradient(70% 120% at 72% 0%, #FDD0E4 0%, transparent 62%), radial-gradient(46% 90% at 100% 100%, #E6DAFF 0%, transparent 62%), radial-gradient(26% 50% at 100% 0%, #DCE5FF 0%, transparent 60%), #FFF5F2',
  ruc: 'radial-gradient(40% 60% at 100% 100%, rgba(185,138,240,.45) 0%, transparent 70%), radial-gradient(45% 55% at 20% 10%, rgba(255,255,255,.28) 0%, transparent 70%), linear-gradient(120deg, #FFC078 0%, #FEA88C 35%, #F78FAE 70%, #EE86C8 100%)',
  dem: 'radial-gradient(70% 90% at 100% 0%, rgba(247,143,174,.5) 0%, transparent 60%), radial-gradient(60% 80% at 0% 100%, rgba(255,176,110,.38) 0%, transparent 60%), radial-gradient(36% 60% at 58% 0%, rgba(139,92,246,.28) 0%, transparent 60%), #1B2150',
  /**
   * Đêm cho thanh bên (cột cao, hẹp): cùng màu / độ mờ / vị trí / điểm tan với `dem`, chỉ đổi
   * kích thước vệt từ % sang px — theo % thì vệt hồng thành một sọc dọc dài mép phải.
   */
  demSidebar:
    'radial-gradient(320px 340px at 100% 0%, rgba(247,143,174,.5) 0%, transparent 60%), radial-gradient(300px 420px at 0% 100%, rgba(255,176,110,.38) 0%, transparent 60%), radial-gradient(160px 260px at 58% 0%, rgba(139,92,246,.28) 0%, transparent 60%), #1B2150',
  /** Viên tab đang chọn; chữ tabActive. */
  tabActive: 'linear-gradient(90deg, #FFDDB5, #FFD0C4, #FDD0E4)',
};

export const fontFamily =
  "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/** Tông màu cho thẻ số liệu / biểu tượng nhóm menu. */
export type Tone = 'accent' | 'success' | 'warning' | 'danger' | 'purple' | 'star' | 'info';

export const toneColors: Record<Tone, { ink: string; soft: string }> = {
  accent: { ink: colors.accent, soft: colors.accentSoft },
  success: { ink: colors.successInk, soft: colors.successSoft },
  warning: { ink: colors.warningInk, soft: colors.warningSoft },
  danger: { ink: colors.dangerInk, soft: colors.dangerSoft },
  purple: { ink: colors.purpleInk, soft: colors.purpleSoft },
  star: { ink: colors.starInk, soft: colors.starSoft },
  info: { ink: colors.infoInk, soft: colors.infoSoft },
};
